import { Alert, Button } from "@mui/material";
import { useMemo, useState } from "react";

import {
  usePropagation,
  useStreamPublish,
  useStreamPublishProgress,
  useUpdatePropagation,
  useUpdatePropagationItem,
} from "@/api/propagation";
import { GeneratedThumbnailGrid } from "@/features/Generations/components/GeneratedThumbnailGrid/GeneratedThumbnailGrid";
import { PublishConfirmationModal } from "@/features/Generations/components/PublishConfirmationModal";
import { useCollectionState } from "@/features/Generations/context/CollectionStateContext";
import {
  COLLECTION_POLL_INTERVAL_MS,
  isCollectionPropagating,
  isCollectionPropagationSettled,
  publishOutcome,
} from "@/features/Generations/propagationList";
import {
  propagationItemId,
  propagationItemsToGeneratedItems,
  propagationItemsToPublishAssets,
  publishableItemsMissingProduct,
} from "@/features/Generations/utils/collectionGridItems";
import { RefreshIcon } from "@/icons";
import { PROPAGATION_STATE } from "@/types/propagation";
import { enqueueErrorSnackbar } from "@/utils/snackbarHelpers";

const SAVE_ITEM_ERROR =
  "Failed to save your choice for this item. Please try again.";

export interface AcceptCollectionStepProps {
  siteId: string;
  onPublished: () => void;
}

export function AcceptCollectionStep({
  siteId,
  onPublished,
}: AcceptCollectionStepProps) {
  const { collectionState } = useCollectionState();
  const [acceptedIds, setAcceptedIds] = useState<string[]>([]);
  const [publishOpen, setPublishOpen] = useState(false);
  const [inlineError, setInlineError] = useState<string | null>(null);
  // Assets a previous run already published; a retry must not resend them.
  const [publishedSlugs, setPublishedSlugs] = useState<ReadonlySet<string>>(
    () => new Set(),
  );

  const slug = collectionState.propagationSlug || "";
  const propagationQuery = usePropagation(siteId, slug, {
    refetchInterval: (query) =>
      isCollectionPropagationSettled(query.state.data)
        ? false
        : COLLECTION_POLL_INTERVAL_MS,
    enabled: !!slug,
  });
  const propagation = propagationQuery.data;

  const publish = useStreamPublish(siteId);
  const publishProgress = useStreamPublishProgress(siteId);
  const propagating = isCollectionPropagating(propagation);
  const { mutate: updatePropagationState } = useUpdatePropagation(siteId, {
    onError: (error) =>
      enqueueErrorSnackbar(
        error,
        "Failed to update the propagation. Please try again.",
      ),
  });

  const { mutate: updateItem } = useUpdatePropagationItem(siteId, slug, {
    onError: (error) => enqueueErrorSnackbar(error, SAVE_ITEM_ERROR),
  });

  // Persist each accept/remove so the review survives leaving the page.
  const toggleItemAccepted = (id: string, accepted: boolean) => {
    const items = propagation?.items ?? [];
    const item = items.find((it, index) => propagationItemId(it, index) === id);
    const itemId = item?.slug || item?.id;
    // No backend identity to PATCH: tell the user instead of a dead click.
    if (!itemId) {
      enqueueErrorSnackbar(null, SAVE_ITEM_ERROR);
      return;
    }
    updateItem({ itemId, body: { is_accepted: accepted } });
  };

  const gridItems = useMemo(
    () => propagationItemsToGeneratedItems(propagation?.items ?? []),
    [propagation?.items],
  );

  const acceptedPublishAssets = propagationItemsToPublishAssets(
    propagation?.items ?? [],
    acceptedIds,
  );
  const pendingPublishAssets = acceptedPublishAssets.filter(
    (publishAsset) => !publishedSlugs.has(publishAsset.asset.slug),
  );
  const alreadyPublishedCount =
    acceptedPublishAssets.length - pendingPublishAssets.length;

  const closeModal = () => {
    // Locked mid-publish: the stream owns the modal until it settles.
    if (publish.isPending) return;
    setPublishOpen(false);
    // Drop a prior error so reopening the modal starts clean.
    publish.reset();
    setInlineError(null);
  };

  const confirmPublish = () => {
    const items = propagation?.items ?? [];
    // Name each item by its grid card caption so the user can find and remove it.
    const missing = publishableItemsMissingProduct(items, acceptedIds);
    if (missing.length > 0) {
      const labels = missing.map((item) => {
        const id = propagationItemId(item, items.indexOf(item));
        const card = gridItems.find((gridItem) => gridItem.id === id);
        return card ? `${card.sequence} · ${card.sku}` : `item ${id}`;
      });
      const one = missing.length === 1;
      setInlineError(
        `Can't publish. ${
          one ? "This item isn't" : "These items aren't"
        } linked to a product: ${labels.join(", ")}. Remove ${
          one ? "it" : "them"
        } and save again to publish the rest.`,
      );
      return;
    }
    setInlineError(null);
    // Publish the rendered assets of the accepted items that haven't published
    // yet, each carrying its product identity (proto PublishAsset).
    const publishAssets = pendingPublishAssets;
    publish.mutate(
      {
        site_id: siteId,
        publish_assets: publishAssets,
      },
      {
        onSuccess: (frames) => {
          const outcome = publishOutcome(
            frames,
            publishAssets.map((publishAsset) => publishAsset.asset.slug),
          );
          setPublishedSlugs(
            (prev) => new Set([...prev, ...outcome.publishedSlugs]),
          );
          // The stream returns 200 even when assets fail; keep the modal open
          // with the reasons so the user can retry the rest.
          if (outcome.error) {
            setInlineError(outcome.error);
            return;
          }
          updatePropagationState({
            slug,
            body: {
              propagation_state: PROPAGATION_STATE.PUBLISHED,
            },
          });
          onPublished();
          setPublishOpen(false);
          publish.reset();
        },
        // Stream errors surface inline in the modal via `publish.isError`; the
        // CTA flips to a retry so the user isn't stranded.
      },
    );
  };

  const publishError =
    inlineError ??
    (publish.isError
      ? publish.error instanceof Error
        ? publish.error.message
        : "Failed to publish the collection. Please try again."
      : null);

  return (
    <>
      <GeneratedThumbnailGrid
        items={gridItems}
        loading={propagationQuery.isLoading}
        saveDisabled={propagating}
        onToggleAccept={toggleItemAccepted}
        notice={
          propagating ? (
            <Alert
              severity="info"
              data-testid="collection-propagating-notice"
              action={
                <Button
                  color="inherit"
                  size="small"
                  startIcon={<RefreshIcon />}
                  disabled={propagationQuery.isFetching}
                  onClick={() => void propagationQuery.refetch()}
                >
                  Refresh
                </Button>
              }
            >
              The collection is still propagating. New propagations appear
              automatically as they finish generating — or refresh to check now.
            </Alert>
          ) : undefined
        }
        onSaveCollection={(ids) => {
          setAcceptedIds(ids);
          // Clear any error/progress left over from a previous attempt.
          publish.reset();
          setInlineError(null);
          setPublishOpen(true);
        }}
      />
      <PublishConfirmationModal
        open={publishOpen}
        acceptedCount={acceptedIds.length - alreadyPublishedCount}
        publishing={publish.isPending}
        progress={
          publishProgress
            ? {
                completed: publishProgress.completed,
                total: publishProgress.total,
              }
            : undefined
        }
        error={publishError}
        onClose={closeModal}
        onConfirm={confirmPublish}
      />
    </>
  );
}
